import { Injectable, OnDestroy, Optional } from '@angular/core';
import {
    DateRange,
    DisplayNotice,
    FlyFreelyError,
    FlyFreelyLoggingService,
    mutateState,
    NoticeCriteria,
    NoticesService,
    patchableState,
    setState,
    Site,
    StateUpdateCommand
} from '@flyfreely-portal-ui/flyfreely';
import { OperationsSearchDataService } from '@flyfreely-portal-ui/operations-shared';
import { SitesDataService } from '@flyfreely-portal-ui/sites-shared';
import { deepEqual } from 'fast-equals';
import {
    catchError,
    combineLatest,
    distinctUntilChanged,
    EMPTY,
    expand,
    filter,
    map,
    Observable,
    of,
    startWith,
    Subject,
    switchMap,
    takeUntil
} from 'rxjs';

export interface SimplifiedLotamDetails {
    lotamId: number;
    dateRange: DateRange;
    name: string;
}

/**
 * Provides a shareable service for holding notice search results. If available,
 * it will use the sites data service to provide the list of available sites,
 * otherwise it will default to not using site lists.
 */
@Injectable()
export class MissionNoticesDataService implements OnDestroy {
    private ngUnsubscribe$ = new Subject<void>();

    private lotamsSource = new Subject<StateUpdateCommand<DisplayNotice[]>>();
    lotams$ = patchableState(this.lotamsSource, [] as DisplayNotice[]);

    constructor(
        operationsSearchDataService: OperationsSearchDataService,
        private noticesService: NoticesService,
        @Optional() sitesDataService: SitesDataService,
        private logging: FlyFreelyLoggingService
    ) {
        combineLatest([
            this.noticesService.change$.pipe(startWith(undefined)),
            sitesDataService != null ? sitesDataService.sites$ : of(undefined),
            operationsSearchDataService.currentSearch$.pipe(
                filter(s => s.organisationId != null),
                distinctUntilChanged(deepEqual)
            )
        ])
            .pipe(
                filter(
                    ([change, sites, search]) =>
                        sites === undefined ||
                        sites.organisationId === search.organisationId
                ),
                // switchMap cancels the in-flight paged load when a new search,
                // site list or notice change arrives, so stale pages are never
                // appended after resetNotices().
                switchMap(([change, sites, search]) => {
                    this.resetNotices();
                    return this.loadNotices(
                        search.time,
                        search.organisationId!,
                        sites?.siteList
                    );
                }),
                takeUntil(this.ngUnsubscribe$)
            )
            .subscribe(notices =>
                this.lotamsSource.next(
                    mutateState(existing => [...existing, ...notices])
                )
            );
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
        this.lotamsSource.complete();
    }

    resetNotices() {
        this.lotamsSource.next(setState([]));
    }

    /**
     * Loads every page of notices that can be shown on the map, emitting one
     * batch per page. Errors are handled here so a failed load never
     * terminates the outer refresh subscription.
     */
    private loadNotices(
        time: DateRange | undefined,
        organisationId: number,
        sites?: Site[]
    ): Observable<DisplayNotice[]> {
        const includedSiteIdList =
            sites != null ? sites.filter(s => !s.archived).map(s => s.id) : [];
        const criteria: NoticeCriteria = {
            organisationId,
            time,
            includedSiteIdList
        };

        return this.noticesService
            .findNotices(criteria, { page: 0, pageSize: 50 })
            .pipe(
                expand(response => {
                    const { page, pageSize } = response.pagination;
                    const lastPage = Math.ceil(response.count / pageSize) - 1;
                    return page < lastPage
                        ? this.noticesService.findNotices(criteria, {
                              page: page + 1,
                              pageSize
                          })
                        : EMPTY;
                }),
                map(notices =>
                    notices.results.filter(n => n.displayLocation != null)
                ),
                catchError((error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error loading notices: ${error.message}`
                    );
                    this.resetNotices();
                    return EMPTY;
                })
            );
    }
}
